import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';
import { FormService } from 'app/services/form.service';

@Component({
  selector: 'app-cas-registry',
  templateUrl: './cas-registry.component.html',
  styleUrls: ['./cas-registry.component.scss']
})
export class CasRegistryComponent implements OnInit {
  @Input({required: true}) substanceType!: SubstanceTypeEnum;
  @Input() shouldShowFullTitle: boolean = true; // Only if single entity/salt shouldn't be displayed on their respective pages.
  casRegistryNumber!: string;

  constructor(private formService: FormService) {}

  ngOnInit(): void {
    this.casRegistryNumber = this.currentFormValue;
  }

  public clearCasRegistryNumberInputField() {
    this.casRegistryNumber = '';
    this.patchValueToCurrentForm('');
    this.errorCheck();
  }

  public updateCasRegistryNumber(event: Event) {
    if (event && event.type === 'blur') {
      var target = document.activeElement;
      if (target && target.nodeValue === "cancel") {
        this.casRegistryNumber = '';
      }
    }
    this.patchValueToCurrentForm(this.casRegistryNumber);
    this.errorCheck();
  }

  public cancelUpdate() {
    this.casRegistryNumber = this.currentFormValue;
    this.errorCheck();
  }

  get currentFormValue() {
    return this.currentForm.get(this.formFieldName)?.value;
  }

  private patchValueToCurrentForm(value: string) {
    this.currentForm.patchValue({ [this.formFieldName] : value });
  }

  get isNoneRegistryNumberInputType() {
    return this.substanceType === SubstanceTypeEnum.None;
  }

  get isSingleEntityRegistryNumberInputType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntity;
  }

  get isSaltRegistryNumberInputType() {
    return this.substanceType === SubstanceTypeEnum.Salt;
  }

  get isRevisionRegistryNumberInputType() { 
    return this.substanceType === SubstanceTypeEnum.Revision;
  }

  get isContactLensMaterial() {
    return this.substanceType === SubstanceTypeEnum.ContactLensMaterial;
  }

  get isBiologicsInputType() {
    return this.substanceType === SubstanceTypeEnum.Biologics;
  }

  get formFieldName() {
    if(this.isNoneRegistryNumberInputType || this.isRevisionRegistryNumberInputType || this.isContactLensMaterial || this.isBiologicsInputType) {
      return 'casRegistryNumber';
    }

    if(this.isSingleEntityRegistryNumberInputType) {
      return 'casRegistryNumberForSingleEntity'; 
    }
    if(this.isSaltRegistryNumberInputType) {
      return 'casRegistryNumberForSaltForm'; 
    }

    return '';
  }

  get formFieldTitle() {
    var title = 'Chemical abstracts service (CAS) registry number';

    if(this.isSingleEntityRegistryNumberInputType && this.shouldShowFullTitle) {
      title += ' for single entity'; 
    }
    if(this.isSaltRegistryNumberInputType && this.shouldShowFullTitle) {
      title += ' for salt form'; 
    }

    return title;
  }

  get infoText() {
    if(this.isFormEType) {
      return `Separate CAS Registry Numbers must be supplied for the polymers and for each monomer. If this is \
        a submission for a hybrid lens material, please supply information for both the soft skirt and hard \
        center.`;
    }

    if(this.isFormFType) {
      return `CAS Registry number must be supplied for the entire substance for proteins, gene therapies,  \
      and other substances to which a CAS registry number can be assigned. If your substance is a cell therapy, \
      or other substance to which a CAS registry number cannot be assigned, please indicate that it “could not be assigned.” \
      You will be asked to attach a letter from CAS, or a copy of the CAS search results later in the application process.`;
    }

    return '';
  }

  private errorCheck() {
    const targetControl = this.currentForm.get(this.formFieldName);
    
    if(!targetControl) {
      return;
    }

    // Force re-validation
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
    });
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  public hasFieldValidationError(fieldName: string, errorName?: string): boolean {
    return this.formService.hasFieldValidationError(fieldName, errorName);
  }

  get isFormEType(): boolean {
    return 'FormE' === this.formType;
  }

  get isFormFType(): boolean {
    return 'FormF' === this.formType;
  }
  
  get formType(): string {
    return this.formService.getCurrentUsanFormType();
  }

  get displayInfoText(): boolean {
    return this.isFormEType || this.isFormFType;
  }
}
